import { ListFilter, Pencil, Plus, Receipt, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCategoriesStore } from '@/features/categories/store';
import { useExpenseEditor } from '@/features/expenses/editor';
import type { ExpenseFilters } from '@/features/expenses/repository';
import { useActiveGroup } from '@/features/groups/store';
import { hasActiveFilters, useLedgerStore } from '@/features/ledger/store';
import { useSettingsStore } from '@/features/settings/store';
import { formatRelativeDate } from '@/lib/date';
import {
  Badge,
  Button,
  CategoryGlyph,
  DataTable,
  EmptyState,
  FilterChip,
  IconButton,
  Input,
  Modal,
  Money,
  PageHeader,
  Select,
  confirm,
  toast,
  type Column,
  type SortDirection,
} from '@/ui';
import { formatMoney, sum } from '@domain/money';
import { SPLIT_TYPES, SPLIT_TYPE_LABELS, type ExpenseWithDetails } from '@domain/types';

type SortKey = 'date' | 'title' | 'amount' | 'paidBy';

export function ExpensesPage() {
  const navigate = useNavigate();
  const openNew = useExpenseEditor((state) => state.openNew);
  const openEdit = useExpenseEditor((state) => state.openEdit);

  const group = useActiveGroup();
  const expenses = useLedgerStore((state) => state.expenses);
  const allExpenses = useLedgerStore((state) => state.allExpenses);
  const filters = useLedgerStore((state) => state.filters);
  const setFilters = useLedgerStore((state) => state.setFilters);
  const resetFilters = useLedgerStore((state) => state.resetFilters);
  const removeExpense = useLedgerStore((state) => state.removeExpense);
  const members = useLedgerStore((state) => state.members);
  const categories = useCategoriesStore((state) => state.categories);
  const pageSize = useSettingsStore((state) => state.pageSize);

  const [search, setSearch] = useState(filters.search ?? '');
  const [filterOpen, setFilterOpen] = useState(false);
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({
    key: 'date',
    direction: 'desc',
  });
  const [page, setPage] = useState(0);

  const currency = group?.currency ?? 'USD';
  const filtersActive = hasActiveFilters(filters);

  // Debounce so each keystroke doesn't fire its own query.
  useEffect(() => {
    if ((filters.search ?? '') === search) return;
    const timer = setTimeout(() => setFilters({ ...filters, search }), 220);
    return () => clearTimeout(timer);
  }, [search, filters, setFilters]);

  // Reset paging when the result set changes. Adjusting state during render is
  // React's documented alternative to a setState-in-effect cascade.
  const viewKey = `${JSON.stringify(filters)}|${sort.key}|${sort.direction}`;
  const [lastViewKey, setLastViewKey] = useState(viewKey);
  if (lastViewKey !== viewKey) {
    setLastViewKey(viewKey);
    setPage(0);
  }

  const sorted = useMemo(() => {
    const factor = sort.direction === 'asc' ? 1 : -1;
    return [...expenses].sort((a, b) => {
      switch (sort.key) {
        case 'title':
          return a.title.localeCompare(b.title) * factor;
        case 'amount':
          return (a.amountMinor - b.amountMinor) * factor;
        case 'paidBy':
          return a.paidByName.localeCompare(b.paidByName) * factor;
        case 'date':
        default:
          return (a.date.localeCompare(b.date) || a.createdAt.localeCompare(b.createdAt)) * factor;
      }
    });
  }, [expenses, sort]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const visible = sorted.slice(page * pageSize, page * pageSize + pageSize);
  const shownTotal = sum(expenses.map((expense) => expense.amountMinor));

  function toggleSort(key: string) {
    setSort((current) =>
      current.key === key
        ? { key: current.key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key: key as SortKey, direction: key === 'title' || key === 'paidBy' ? 'asc' : 'desc' }
    );
  }

  async function handleDelete(expense: ExpenseWithDetails) {
    const ok = await confirm({
      title: `Delete "${expense.title}"?`,
      message: 'Balances update immediately. This cannot be undone.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;

    await removeExpense(expense.id);
    toast.success('Expense deleted.');
  }

  function clearAll() {
    setSearch('');
    resetFilters();
  }

  if (!group) {
    return (
      <>
        <PageHeader title="Expenses" />
        <EmptyState
          icon={Receipt}
          title="No group selected"
          description="Choose a group to see its expenses."
          action={<Button onClick={() => navigate('/groups')}>Choose a group</Button>}
        />
      </>
    );
  }

  const columns: Column<ExpenseWithDetails>[] = [
    {
      key: 'title',
      header: 'Expense',
      sortable: true,
      cell: (row) => (
        <span className="flex items-center gap-2.5">
          <CategoryGlyph icon={row.categoryIcon} color={row.categoryColor} size="sm" />
          <span className="min-w-0">
            <span className="block truncate font-medium">{row.title}</span>
            <span className="block truncate text-[12px] text-muted">
              {row.categoryName ?? 'Uncategorised'} · {SPLIT_TYPE_LABELS[row.splitType]}
            </span>
          </span>
        </span>
      ),
    },
    {
      key: 'paidBy',
      header: 'Paid by',
      sortable: true,
      hideOnNarrow: true,
      cell: (row) => <span className="text-muted">{row.paidByName}</span>,
    },
    {
      key: 'people',
      header: 'Split',
      align: 'center',
      hideOnNarrow: true,
      width: '80px',
      cell: (row) => <Badge>{row.participants.length}</Badge>,
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      align: 'right',
      width: '130px',
      cell: (row) => <span className="text-muted">{formatRelativeDate(row.date)}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      align: 'right',
      width: '130px',
      cell: (row) => (
        <Money minor={row.amountMinor} currency={currency} className="font-semibold" />
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '84px',
      cell: (row) => (
        <span
          className="flex justify-end gap-0.5"
          onClick={(event) => event.stopPropagation()}
        >
          <IconButton
            icon={Pencil}
            label={`Edit ${row.title}`}
            size="sm"
            onClick={() => openEdit(row.id)}
          />
          <IconButton
            icon={Trash2}
            label={`Delete ${row.title}`}
            size="sm"
            onClick={() => handleDelete(row)}
          />
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Expenses"
        eyebrow={group.name}
        description={`Showing ${expenses.length} of ${allExpenses.length} · ${formatMoney(
          shownTotal,
          currency
        )}`}
        actions={
          <Button icon={Plus} onClick={openNew}>
            Add expense
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          data-search-input
          icon={Search}
          placeholder="Search title, notes, category or payer…   ( / )"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          containerClassName="min-w-56 flex-1"
          autoComplete="off"
        />
        <Button
          variant={filtersActive ? 'primary' : 'secondary'}
          icon={ListFilter}
          onClick={() => setFilterOpen(true)}
        >
          Filters
        </Button>
        {(filtersActive || search) && (
          <Button variant="ghost" icon={X} onClick={clearAll}>
            Clear
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={visible}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/expenses/${row.id}`)}
        sort={sort}
        onSortChange={toggleSort}
        empty={
          allExpenses.length === 0 ? (
            <EmptyState
              icon={Receipt}
              title="No expenses yet"
              description={`Record what people spend in ${group.name} and balances update automatically.`}
              action={
                <Button icon={Plus} onClick={openNew}>
                  Add expense
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={Search}
              title="Nothing matches"
              description="Try a different search term or loosen the filters."
              action={<Button onClick={clearAll}>Clear filters</Button>}
            />
          )
        }
      />

      {sorted.length > pageSize && (
        <div className="mt-3 flex items-center justify-between gap-3 text-[13px] text-muted">
          <span>
            Showing {page * pageSize + 1}–{Math.min((page + 1) * pageSize, sorted.length)} of{' '}
            {sorted.length}
          </span>
          <span className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={page === 0}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </Button>
            <span>
              Page {page + 1} of {pageCount}
            </span>
            <Button
              size="sm"
              variant="secondary"
              disabled={page + 1 >= pageCount}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </span>
        </div>
      )}

      {filterOpen && (
        <FiltersModal
          onClose={() => setFilterOpen(false)}
          filters={filters}
          onApply={(next) => {
            setFilters({ ...next, search });
            setFilterOpen(false);
          }}
          categories={categories}
          members={members}
        />
      )}
    </>
  );
}

function FiltersModal({
  onClose,
  filters,
  onApply,
  categories,
  members,
}: {
  onClose: () => void;
  filters: ExpenseFilters;
  onApply: (filters: ExpenseFilters) => void;
  categories: { id: string; name: string; color: string }[];
  members: { id: string; name: string }[];
}) {
  // Mounted only while open (see the call site), so the initialiser is enough
  // to seed the draft from the live filters each time it is reopened.
  const [draft, setDraft] = useState<ExpenseFilters>(filters);
  const pageSize = useSettingsStore((state) => state.pageSize);
  const setPageSize = useSettingsStore((state) => state.setPageSize);

  function toggle(key: 'categoryIds' | 'memberIds' | 'splitTypes', value: string) {
    setDraft((current) => {
      const list = (current[key] as string[] | undefined) ?? [];
      const next = list.includes(value)
        ? list.filter((item) => item !== value)
        : [...list, value];
      return { ...current, [key]: next.length ? next : undefined };
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Filter expenses"
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={() => setDraft({})}>
            Reset
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onApply(draft)}>Apply filters</Button>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-muted">Categories</legend>
          <div className="flex flex-wrap gap-1.5">
            <FilterChip
              label="Uncategorised"
              selected={Boolean(draft.uncategorisedOnly)}
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  uncategorisedOnly: current.uncategorisedOnly ? undefined : true,
                  categoryIds: undefined,
                }))
              }
            />
            {categories.map((category) => (
              <FilterChip
                key={category.id}
                label={category.name}
                color={category.color}
                selected={draft.categoryIds?.includes(category.id) ?? false}
                onClick={() => {
                  setDraft((current) => ({ ...current, uncategorisedOnly: undefined }));
                  toggle('categoryIds', category.id);
                }}
              />
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-muted">
            People (paid or shared)
          </legend>
          <div className="flex flex-wrap gap-1.5">
            {members.map((member) => (
              <FilterChip
                key={member.id}
                label={member.name}
                selected={draft.memberIds?.includes(member.id) ?? false}
                onClick={() => toggle('memberIds', member.id)}
              />
            ))}
            {members.length === 0 && <p className="text-[13px] text-muted">No members yet.</p>}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-2 text-[13px] font-medium text-muted">Split type</legend>
          <div className="flex flex-wrap gap-1.5">
            {SPLIT_TYPES.map((splitType) => (
              <FilterChip
                key={splitType}
                label={SPLIT_TYPE_LABELS[splitType]}
                selected={draft.splitTypes?.includes(splitType) ?? false}
                onClick={() => toggle('splitTypes', splitType)}
              />
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="From"
            type="date"
            value={draft.dateFrom ?? ''}
            onChange={(event) =>
              setDraft((current) => ({ ...current, dateFrom: event.target.value || undefined }))
            }
          />
          <Input
            label="To"
            type="date"
            value={draft.dateTo ?? ''}
            onChange={(event) =>
              setDraft((current) => ({ ...current, dateTo: event.target.value || undefined }))
            }
          />
        </div>

        <Select
          label="Rows per page"
          value={String(pageSize)}
          onChange={(event) => setPageSize(Number(event.target.value))}
          options={[10, 25, 50, 100].map((size) => ({
            value: String(size),
            label: `${size} rows`,
          }))}
          containerClassName="max-w-40"
        />
      </div>
    </Modal>
  );
}
